import { apiFetch } from './client';

export type PipelineResponseTarget = 'telegram' | 'outbound_webhook' | 'both' | 'none';

export interface PipelineTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string;
  definition: Record<string, unknown>;
  is_system: boolean;
  enabled: boolean;
}

export interface PipelineInstance {
  id: string;
  template_id: string | null;
  agent_id: string | null;
  agent_chain?: string[] | null;
  name: string;
  description: string | null;
  trigger_type: string;
  response_target: PipelineResponseTarget;
  response_webhook_url: string | null;
  external_conversation_key: string;
  message_field: string;
  reply_url_field: string;
  config: Record<string, unknown>;
  schedule_cron: string | null;
  schedule_timezone: string;
  schedule_payload: Record<string, unknown>;
  next_run_at: string | null;
  last_scheduled_at: string | null;
  enabled: boolean;
  admin_paused?: boolean;
  admin_paused_reason?: string | null;
  last_fired_at: string | null;
  created_at: string;
  updated_at: string;
  token?: string;
  trigger_url?: string;
}

export interface PipelineRunEvent {
  id: string;
  pipeline_run_id: string;
  type: string;
  message: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

export interface PipelineRun {
  id: string;
  pipeline_id: string | null;
  telegram_id: number;
  agent_run_id: string | null;
  trigger_type: string;
  external_conversation_id: string | null;
  status: 'pending' | 'running' | 'done' | 'failed' | 'cancelled';
  input: Record<string, unknown>;
  normalized_event: Record<string, unknown>;
  output: Record<string, unknown>;
  error: string | null;
  created_at: string;
  updated_at: string;
  events?: PipelineRunEvent[];
}

export interface PipelineCreateInput {
  templateId?: string | null;
  agentId: string;
  name: string;
  description?: string | null;
  responseTarget: PipelineResponseTarget;
  responseWebhookUrl?: string | null;
  externalConversationKey: string;
  messageField: string;
  replyUrlField: string;
  signingSecret?: string | null;
  triggerType?: 'inbound_webhook' | 'schedule';
  scheduleCron?: string | null;
  scheduleTimezone?: string;
  schedulePayload?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export interface PipelinePatchInput {
  name?: string;
  description?: string | null;
  agentId?: string | null;
  agentChain?: string[];
  responseTarget?: PipelineResponseTarget;
  responseWebhookUrl?: string | null;
  externalConversationKey?: string;
  messageField?: string;
  replyUrlField?: string;
  signingSecret?: string | null;
  triggerType?: 'inbound_webhook' | 'schedule';
  scheduleCron?: string | null;
  scheduleTimezone?: string;
  schedulePayload?: Record<string, unknown>;
  config?: Record<string, unknown>;
  variables?: { key: string; label?: string; value: string }[];
  enabled?: boolean;
}

export interface PipelineVersion {
  id: string;
  label: string | null;
  created_at: string;
}

export const pipelinesApi = {
  templates: () => apiFetch<PipelineTemplate[]>('/api/pipelines/templates'),
  list: () => apiFetch<PipelineInstance[]>('/api/pipelines'),
  create: (data: PipelineCreateInput) => apiFetch<PipelineInstance>('/api/pipelines', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: PipelinePatchInput) => apiFetch<PipelineInstance>(`/api/pipelines/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  regenerateToken: (id: string) => apiFetch<PipelineInstance>(`/api/pipelines/${id}/regenerate-token`, { method: 'POST' }),
  remove: (id: string) => apiFetch<{ deleted: boolean; id: string }>(`/api/pipelines/${id}`, { method: 'DELETE' }),
  runs: (id: string) => apiFetch<PipelineRun[]>(`/api/pipelines/${id}/runs`),
  test: (id: string, data?: { message?: string; payload?: Record<string, unknown> }) => apiFetch<{ ok: boolean; test: boolean; pipelineRunId: string; runId: string }>(`/api/pipelines/${id}/test`, { method: 'POST', body: JSON.stringify(data ?? {}) }),
  // История версий (последние 3, не старше 24ч) и откат на одну из них.
  versions: (id: string) => apiFetch<PipelineVersion[]>(`/api/pipelines/${id}/versions`),
  restoreVersion: (id: string, versionId: string) => apiFetch<PipelineInstance & { restoreWarning?: string }>(`/api/pipelines/${id}/versions/${versionId}/restore`, { method: 'POST' }),
};

// ── Automation graph (n8n-подобный редактор) ─────────────────────────────
export type GraphNodeKind = 'trigger' | 'agent' | 'tool' | 'response';

export interface AutomationGraphNode {
  id: string;
  kind: GraphNodeKind;
  label: string;
  position: { x: number; y: number };
  data: Record<string, unknown>;
}

export interface AutomationGraphEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

export interface AutomationGraph {
  pipelineId: string;
  pipelineName: string;
  editable: boolean;
  nodes: AutomationGraphNode[];
  edges: AutomationGraphEdge[];
  catalog: Array<{ id: string; label: string; description?: string }>;
}

export const automationGraphApi = {
  get: (id: string) => apiFetch<AutomationGraph>(`/api/pipelines/${id}/graph`),
  save: (id: string, nodes: AutomationGraphNode[]) =>
    apiFetch<AutomationGraph>(`/api/pipelines/${id}/graph`, { method: 'PUT', body: JSON.stringify({ nodes }) }),
};

// Ассистент-оркестратор внутри карточки автоматизации: диагностика по логам + перенастройка через чат.
export interface AutomationAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AutomationAssistantResponse {
  ok: boolean;
  reply: string;
  proposedGraph: { nodes: AutomationGraphNode[] } | null;
  proposedAgentChain?: string[] | null;
  modelTier?: string;
  runsAnalyzed?: number;
}

export const automationAssistantApi = {
  chat: (id: string, message: string, history: AutomationAssistantMessage[]) =>
    // Ассистент читает схему сценария и логи последних запусков, поэтому модель
    // думает дольше обычного запроса. Дефолтные 30 с обрывали ответ на середине,
    // и пользователь видел «Не удалось получить ответ ассистента».
    apiFetch<AutomationAssistantResponse>(`/api/pipelines/${id}/assistant`, {
      method: 'POST',
      body: JSON.stringify({ message, history }),
      _timeoutMs: 120_000,
    } as RequestInit),
};
